import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { api } from '@/lib/api';
import { friendlyError } from '@/lib/errors';

/**
 * SignupBlockedModal — surfaced when any signup path returns
 * SIGNUPS_DISABLED (HandleSignup, OAuth signup branch). Captures
 * interested emails into the waitlist table; the admin exports the
 * list at launch for bulk announcements.
 *
 * UX rules:
 *  - Backdrop click + Escape close.
 *  - Email pre-filled when the user already typed one on the signup
 *    form (passed via `prefillEmail`).
 *  - Idempotent on email — re-submitting the same address is a no-op
 *    on the server side; we still show the "thanks" state.
 */
export function SignupBlockedModal({
  open,
  prefillEmail,
  onClose,
}: {
  open: boolean;
  prefillEmail?: string;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(prefillEmail ?? '');
  const [state, setState] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail(prefillEmail ?? '');
      setState('idle');
      setError(null);
    }
  }, [open, prefillEmail]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === 'submitting') return;
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Enter a valid email.');
      return;
    }
    setState('submitting');
    setError(null);
    try {
      await api.waitlist({ email: trimmed, source: 'signup_gate' });
      setState('done');
    } catch (err) {
      setError(friendlyError(err, 'waitlist'));
      setState('idle');
    }
  };

  return createPortal(
    <div style={backdrop} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={closeX}
        >
          ×
        </button>
        <div className="mono" style={eyebrow}>SOON</div>
        <h2 className="serif" style={title}>
          We&rsquo;re not open yet.
        </h2>
        <p style={body}>
          WYDM isn&rsquo;t accepting sign-ups just yet — we&rsquo;re wrapping up the last bits before launch. Leave your email and we&rsquo;ll ping you the moment doors open.
        </p>
        {state === 'done' ? (
          <div style={doneBox}>
            <strong>You&rsquo;re on the list.</strong> We&rsquo;ll be in touch.
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={input}
            />
            {error && <div style={errorLine}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button type="button" onClick={onClose} style={cancelBtn}>Not now</button>
              <button type="submit" disabled={state === 'submitting'} style={primaryBtn}>
                {state === 'submitting' ? 'Saving…' : 'Notify me'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(43, 29, 20, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: 16,
};

const card: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 22,
  padding: '32px 28px 28px',
  width: '100%',
  maxWidth: 460,
  boxShadow: '0 28px 64px rgba(0,0,0,0.32)',
  position: 'relative',
};

const closeX: React.CSSProperties = {
  position: 'absolute',
  top: 14,
  right: 14,
  width: 32,
  height: 32,
  borderRadius: '50%',
  border: 'none',
  background: 'transparent',
  color: 'var(--ink-3)',
  fontSize: 22,
  cursor: 'pointer',
  lineHeight: 1,
};

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: 2,
  color: 'var(--coral)',
  textTransform: 'uppercase',
  marginBottom: 8,
};

const title: React.CSSProperties = {
  fontSize: 30,
  fontWeight: 500,
  fontStyle: 'italic',
  margin: '0 0 12px',
  color: 'var(--ink)',
};

const body: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--ink-2)',
  margin: '0 0 22px',
};

const input: React.CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  padding: '12px 14px',
  fontSize: 14,
  fontFamily: 'var(--font-ui)',
  outline: 'none',
  color: 'var(--ink)',
};

const errorLine: React.CSSProperties = {
  fontSize: 12,
  color: '#a8331f',
};

const cancelBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--line)',
  color: 'var(--ink-3)',
  borderRadius: 100,
  padding: '9px 16px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};

const primaryBtn: React.CSSProperties = {
  background: 'var(--coral)',
  color: '#fff',
  border: 'none',
  borderRadius: 100,
  padding: '10px 22px',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--font-ui)',
};

const doneBox: React.CSSProperties = {
  background: 'rgba(56,142,60,0.12)',
  color: '#2e7d32',
  borderRadius: 12,
  padding: '14px 16px',
  fontSize: 14,
};
